import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "../utils/api";
import Helper from "../utils/helper";

export const usePreDefinedListStore = defineStore("pre_defined_list_store", {
  persist: true,
  state: () => ({
    preDefinedFullList: []
  }),
  actions: {
    // Create a new row in pre_defined_lists. The id is generated client-side
    // so the caller can wire it into the field config (`options_ref`) without
    // waiting for the round-trip. Online-only, like the form builder it's
    // created from — see Api.PreDefinedList.createPreDefinedList.
    async createPreDefinedList({ description, list }) {
      const id = Helper.getRandomUuid();
      const payload = {
        id,
        description,
        list: Array.isArray(list) ? list : [],
        local_updated_at: new Date().toISOString()
      };
      const res = await Api.PreDefinedList.createPreDefinedList(payload);
      if (res?.error) throw new Error(res.error);
      // Supabase returns the inserted row in `data`; fall back to the
      // payload (with our FE id) if the response shape is empty.
      const row = res?.data ?? { ...payload };
      const exists = this.preDefinedFullList.some((r) => r.id === row.id);
      if (!exists) this.preDefinedFullList = [...this.preDefinedFullList, row];
      return row;
    },
    // Replace description + items on an existing row. Affects every template
    // that references the list — the caller is expected to have confirmed.
    async updatePreDefinedListMeta({ id, description, list }) {
      const payload = {
        id,
        description,
        list: Array.isArray(list) ? list : [],
        local_updated_at: new Date().toISOString()
      };
      const { error } = await Api.PreDefinedList.updatePreDefinedList(payload);
      if (error) throw error;
      const idx = this.preDefinedFullList.findIndex((r) => r.id === id);
      if (idx !== -1) {
        const next = [...this.preDefinedFullList];
        next[idx] = { ...next[idx], description, list: payload.list };
        this.preDefinedFullList = next;
      }
      return payload;
    },
    async getPreDefinedListData(
      config,
      customTableName = null,
      offlineFirst = true
    ) {
      let _res = await Api.PreDefinedList.getPreDefinedLists(
        config,
        customTableName,
        offlineFirst
      );
      if (customTableName) {
        return _res;
      }
      this.preDefinedFullList = _res.data || [];
      return _res;
    },
    async getPreDefinedListById(id, offlineFirst = true) {
      if (this.preDefinedFullList.length === 0) {
        await this.getPreDefinedListData(
          { fetchAll: true },
          null,
          offlineFirst
        );
      }
      let _res = this.preDefinedFullList.find((item) => item.id === id) || null;
      let _records = [];
      if (_res) {
        _records = _res.list;
      }
      return _records;
    },
    async newPreDefinedListItem(listId, record) {
      // Find the list in the store
      const existingList = this.preDefinedFullList.find(
        (item) => item.id === listId
      );
      if (!existingList) {
        throw new Error("List not found");
      }

      // Snapshot the previous list so we can roll back the optimistic mutation
      // if the API call fails — otherwise a server-side error leaves a phantom
      // entry in the cache and the rendered dropdown shows a ghost row until
      // the page is reloaded.
      const previousList = existingList.list;
      const updatedRecords = [...previousList, record];

      // Optimistic local update
      existingList.list = updatedRecords;

      // Update database
      const { error } = await Api.PreDefinedList.updatePreDefinedList({
        id: listId,
        list: updatedRecords
      });

      if (error) {
        // Roll back the optimistic mutation before surfacing the error.
        existingList.list = previousList;
        console.error("Failed to add new item to pre-defined list:", error);
        throw error;
      }
    },
    async updatePreDefinedListItem(listId, record) {
      // Find the list in the store
      const existingList = this.preDefinedFullList.find(
        (item) => item.id === listId
      );
      if (!existingList) {
        throw new Error("List not found");
      }

      const previousList = existingList.list;
      const updatedRecords = previousList.map((item) =>
        item.id === record.id ? record : item
      );

      // Optimistic local update
      existingList.list = updatedRecords;

      // Update database
      const { error } = await Api.PreDefinedList.updatePreDefinedList({
        id: listId,
        list: updatedRecords
      });

      if (error) {
        existingList.list = previousList;
        console.error("Failed to update item in pre-defined list:", error);
        throw error;
      }
    },
    async deletePreDefinedListItem(listId, recordId) {
      // Find the list in the store
      const existingList = this.preDefinedFullList.find(
        (item) => item.id === listId
      );
      if (!existingList) {
        throw new Error("List not found");
      }

      const previousList = existingList.list;
      const updatedRecords = previousList.filter(
        (item) => item.id !== recordId
      );

      // Optimistic local update
      existingList.list = updatedRecords;

      // Update database
      const { error } = await Api.PreDefinedList.updatePreDefinedList({
        id: listId,
        list: updatedRecords
      });

      if (error) {
        existingList.list = previousList;
        console.error("Failed to delete item from pre-defined list:", error);
        throw error;
      }
    }
  },
  $reset() {
    this.preDefinedFullList = [];
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePreDefinedListStore, import.meta.hot)
  );
}
